import React from "react";
import type { UseQueryResult } from "react-query";
import type { AxiosResponse, AxiosError } from "axios";
import memoize from "memoizee";
import debounce from "lodash/debounce";
import { dequal } from "dequal/lite";

import {
  FieldEnrollmentsNetworkResponse,
  FieldEnrollmentsDataEnvelope,
} from "../../../models/enrollments";
import activities from "../../../redux/activities";
import { FetcherQueryKey, QueryFunction } from "../../../services/query";
import { ensureValue } from "../../../services/util";
import enrollmentsService from "../../../services/enrollments";
import { numberOfFieldEnrollmentsListsPossiblyActiveAtTheSameTime } from "../../../services/helpers/refinements";

import type { ResultOfUseSetFieldEnrollmentsGlobalActivityAndStatus } from "./fieldEnrollments.globalStatus";

export type FieldEnrollmentsFetcherParams = {
  userId: string | undefined;
  operationId: string | undefined;
  catalogFlag: boolean | undefined;
};
export type FieldEnrollmentsQueryKey =
  FetcherQueryKey<FieldEnrollmentsFetcherParams>;

export const fieldEnrollmentsQueryKeyPrefix =
  "[@fabric/core][fieldEnrollments]";

/**
 * Computes the fieldEnrollmentsQueryKey from the given params.
 */
export const computeFieldEnrollmentsQueryKey = (
  params: FieldEnrollmentsFetcherParams,
): FieldEnrollmentsQueryKey => [fieldEnrollmentsQueryKeyPrefix, params];

/**
 * Fetches the fieldEnrollments using the enrollmentsService.
 */
export const fieldEnrollmentsFetcher: QueryFunction<
  AxiosResponse<FieldEnrollmentsNetworkResponse>,
  FieldEnrollmentsFetcherParams
> = async (context) => {
  const userId = ensureValue(context.queryKey[1].userId, "userId");
  const operationId = ensureValue(
    context.queryKey[1].operationId,
    "operationId",
  );
  const catalogFlag = ensureValue(
    context.queryKey[1].catalogFlag,
    "catalogFlag",
  );
  return enrollmentsService.getFieldEnrollments(
    { userId, operationId },
    catalogFlag,
  );
};

/**
 * Compute the fieldEnrollments data envelope.
 *
 * The memoization of this function may seem useless
 * but it is very important as it keeps the same object reference
 * between renders and across hook instances.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeDataEnvelope = memoize(
  (
    fieldEnrollmentsByFeature: FieldEnrollmentsDataEnvelope["fieldEnrollmentsByFeature"],
  ): FieldEnrollmentsDataEnvelope => {
    return { fieldEnrollmentsByFeature };
  },
  {
    max: numberOfFieldEnrollmentsListsPossiblyActiveAtTheSameTime,
    profileName: "[fieldEnrollments.query.helpers] computeDataEnvelope",
  },
);

/**
 * Compute the data envelope from the response.
 *
 * This is only for use in computeResultOfUseQuery.
 * Otherwise, the memoization will not function properly.
 *
 * There is no need for this function itself to be memoized
 * because its called functions are memoized and its result is memoized.
 */
export const computeDataEnvelopeFromResponse = (
  fieldEnrollmentsAxiosResponse: AxiosResponse<FieldEnrollmentsNetworkResponse>,
): FieldEnrollmentsDataEnvelope => {
  // Compute the derived data for the data envelope.
  // (Don't worry. these compute functions are globally memoized.)
  const dataEnvelope = computeDataEnvelope(fieldEnrollmentsAxiosResponse.data);
  return dataEnvelope;
};

/**
 * Compute the result of useQuery,
 * converting from the response data to the data envelope.
 *
 * This is only for use in useFieldEnrollmentsQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeResultOfUseQuery = (
  resultOfUseQuery: UseQueryResult<
    AxiosResponse<FieldEnrollmentsNetworkResponse>,
    AxiosError
  >,
): UseQueryResult<FieldEnrollmentsDataEnvelope, AxiosError> => {
  const dataEnvelope = resultOfUseQuery.data
    ? computeDataEnvelopeFromResponse(resultOfUseQuery.data)
    : undefined;

  return {
    ...resultOfUseQuery,
    data: dataEnvelope,
  } as UseQueryResult<FieldEnrollmentsDataEnvelope, AxiosError>;
};

/**
 * Returns whether the current list of field enrollments is equal to the previous.
 *
 * This does a full deep comparison of the field enrollments data.
 *
 * This is run once per network response as opposed to once per hook instance.
 *
 * This is only meant for use in useFieldEnrollmentsQuery.
 */
export const isDataEqual = (
  prevResponse: AxiosResponse<FieldEnrollmentsNetworkResponse> | undefined,
  response: AxiosResponse<FieldEnrollmentsNetworkResponse>,
): boolean => {
  // TODO: Replace with an etag comparison once the endpoint provides it.

  // Deep compare entire field enrollments data object.
  return dequal(prevResponse?.data, response.data);
};

/**
 * The returned value of this will contain
 * the React Query onSuccess function for useFieldEnrollmentsQuery
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useFieldEnrollmentsQuery.
 *
 * This is only meant for use in useFieldEnrollmentsQuery.
 */
export const onSuccess = debounce(
  ({
    setFieldEnrollmentsGlobalActivityAndStatus,
  }: {
    setFieldEnrollmentsGlobalActivityAndStatus: ResultOfUseSetFieldEnrollmentsGlobalActivityAndStatus;
  }) => {
    setFieldEnrollmentsGlobalActivityAndStatus({
      status: "success",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the React Query onError function for useFieldEnrollmentsQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useFieldEnrollmentsQuery.
 *
 * This is only meant for use in useFieldEnrollmentsQuery.
 */
export const onError = debounce(
  ({
    setFieldEnrollmentsGlobalActivityAndStatus,
  }: {
    setFieldEnrollmentsGlobalActivityAndStatus: ResultOfUseSetFieldEnrollmentsGlobalActivityAndStatus;
  }) => {
    setFieldEnrollmentsGlobalActivityAndStatus({
      status: "error",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the function to call when loading in useFieldEnrollmentsQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useFieldEnrollmentsQuery.
 *
 * This is only meant for use in useFieldEnrollmentsQuery.
 */
export const onLoading = debounce(
  ({
    setFieldEnrollmentsGlobalActivityAndStatus,
  }: {
    setFieldEnrollmentsGlobalActivityAndStatus: ResultOfUseSetFieldEnrollmentsGlobalActivityAndStatus;
  }) => {
    setFieldEnrollmentsGlobalActivityAndStatus({
      status: "loading",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * This hook contains the logic to run in useFieldEnrollmentsQuery when loading.
 *
 * This hook is only meant for use in useFieldEnrollmentsQuery.
 */
export const useHandleLoadingStatus = ({
  resultOfUseQuery,
  setFieldEnrollmentsGlobalActivityAndStatus,
}: {
  resultOfUseQuery: UseQueryResult;
  setFieldEnrollmentsGlobalActivityAndStatus: ResultOfUseSetFieldEnrollmentsGlobalActivityAndStatus;
}): void => {
  const isStatusLoading = resultOfUseQuery.status === "loading";

  // Why a useEffect? So that this only sets the current operation sub data global
  // activity and status when these values change, as opposed to on
  // every render when we happen to be loading. This is a pain but is only
  // necessary to do while we preserve the behavior the deprecated data hooks.
  React.useEffect(() => {
    if (isStatusLoading) {
      onLoading({ setFieldEnrollmentsGlobalActivityAndStatus });
    }
  }, [isStatusLoading, setFieldEnrollmentsGlobalActivityAndStatus]);
};
